import type {
  HubspotOauthStatus,
  StripeOauthStatus,
  TenantSettingsResponse,
} from '../services/api';
import type { DealMapping } from '../types/api';

/** Visual/logical state of a single setup step. */
export type StepState = 'done' | 'todo' | 'loading' | 'info';

/** Step 2 has no reliable completion signal; it is always informational. */
export const STEP2_STATE: StepState = 'info';

/**
 * Resolve a live step's state. While its data is loading we show 'loading'
 * (never a false check); on error we degrade to 'todo' rather than blocking.
 */
function resolveState(loading: boolean, error: boolean, done: boolean): StepState {
  if (loading) return 'loading';
  if (error) return 'todo';
  return done ? 'done' : 'todo';
}

/** Step 1 — both HubSpot and Stripe OAuth connections are live. */
export function step1State(args: {
  hubspot?: HubspotOauthStatus;
  stripe?: StripeOauthStatus;
  loading: boolean;
  error: boolean;
}): StepState {
  const done = !!args.hubspot?.connected && !!args.stripe?.connected;
  return resolveState(args.loading, args.error, done);
}

/** Step 3 — automatic billing sync is enabled. */
export function step3State(args: {
  settings?: TenantSettingsResponse;
  loading: boolean;
  error: boolean;
}): StepState {
  const done = args.settings?.auto_sync === true;
  return resolveState(args.loading, args.error, done);
}

/** Step 4 — at least one deal has synced or linked into Stripe. */
export function step4State(args: {
  deals?: DealMapping[];
  loading: boolean;
  error: boolean;
}): StepState {
  const done = (args.deals ?? []).some(
    (d) => d.sync_status === 'synced' || d.sync_status === 'linked',
  );
  return resolveState(args.loading, args.error, done);
}
